import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Table,
  Progress,
  Spinner,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";

// react table
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

// redux
import { shallowEqual, useSelector } from "react-redux";

// hooks
import { useProfile } from "../../Components/Hooks/UserHooks";
import { SALES_ENABLED } from "../../common/featureFlags";

// router
import { Link, useNavigate } from "react-router-dom";

// diğer importlar
import moment from "moment/moment";

// Column Definitions
const columnHelper = createColumnHelper();

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input
      {...props}
      value={value}
      id="search-bar-0"
      className="form-control border-0 search"
      autoComplete="off"
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      }}
    />
  );
};

const ExpiringAgreementsView = ({ onBack }) => {
  const { userProfile } = useProfile();
  const navigate = useNavigate();

  // Redux state
  const { expiringAgreements, expiringAgreementsLoading } = useSelector(
    (state) => {
      return {
        expiringAgreements: state.dashboard.expiringAgreements,
        expiringAgreementsLoading: state.dashboard.expiringAgreementsLoading,
      };
    },
    shallowEqual
  );

  // States
  const [data, setData] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");

  // Filtreleme işlemi
  useEffect(() => {
    setData([]);

    const filteredData = expiringAgreements?.filter((item) => {
      if (
        globalFilter &&
        !Object.values(item).some(
          (value) =>
            typeof value === "string" &&
            (value
              .toLocaleLowerCase()
              .includes(globalFilter.toLocaleLowerCase()) ||
              value
                .toLocaleUpperCase()
                .includes(globalFilter.toLocaleUpperCase()))
        )
      )
        return false;

      return true;
    });

    setData(filteredData || []);
  }, [globalFilter, expiringAgreements]);

  // Uzatma butonuna tıklama - Paketler sayfasına customerId ve araç bilgileri ile yönlendir
  const handleExtendClick = (rowData) => {
    if (!SALES_ENABLED) return;

    navigate("/packages", {
      state: {
        customerId: rowData.CustomerId,
        customerName: rowData.CustomerName,
        isRenewal: true,
        // Araç bilgileri
        carInfo: {
          plate: rowData.CarPlate,
          brand: rowData.CarBrand,
          model: rowData.CarModel,
          modelYear: rowData.CarYear,
          usageType: rowData.CarCategory,
        },
      },
    });
  };

  // Columns
  const columns = useMemo(
    () => [
      columnHelper.accessor("actions1", {
        header: "",
        size: 46,
        minSize: 46,
        maxSize: 46,
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                tag="button"
                className="btn btn-sm btn-ghost-secondary btn-icon"
              >
                <i className="ri-more-2-fill fs-16"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end" strategy="fixed">
                <DropdownItem
                  onClick={() => handleExtendClick(row.original)}
                  disabled={!SALES_ENABLED}
                  title={
                    SALES_ENABLED
                      ? "Sözleşmeyi yenile"
                      : "Yenileme işlemleri geçici olarak kapalı"
                  }
                >
                  <i className="ri-file-copy-2-line align-middle me-1 text-primary"></i>{" "}
                  {SALES_ENABLED
                    ? "Sözleşme Yenile"
                    : "Yenileme Geçici Olarak Kapalı"}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      }),
      columnHelper.accessor("AggreementName", {
        header: "Sözleşme Bilgileri",
        size: "auto",
        minSize: 250,
        maxSize: 350,
        enableResizing: false,
        cell: ({ row }) => {
          return (
            <div className="text-truncate fs-16 fw-semibold">
              {row.original.AggreementName}
              <div className="text-truncate fw-medium fs-12">
                {row.original.AggrementNo}
              </div>
              <div className="text-truncate fw-medium fs-12">
                {moment(row.original.CreateDatetime).format("DD.MM.YYYY")}
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor("Price", {
        header: "Fiyat",
        size: 120,
        minSize: 120,
        maxSize: 120,
        cell: ({ row }) => {
          return (
            <div className="text-truncate fw-medium fs-12">
              <b className="text-dark">
                {new Intl.NumberFormat("tr-TR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(row.original.Price)}{" "}
                TL
              </b>
            </div>
          );
        },
      }),
      columnHelper.accessor("AgencyName", {
        header: "Acente",
        size: 150,
        minSize: 150,
        maxSize: 150,
        cell: ({ row }) => {
          return (
            <div>
              <div className="text-truncate fw-semibold fs-12">
                {row.original.AgencyName ? row.original.AgencyName : "-"}
              </div>
              <div className="text-truncate fw-semibold fs-12">
                {row.original.District ? row.original.District : "-"}/
                {row.original.City ? row.original.City : "-"}
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor("CustomerName", {
        header: "Müşteri Bilgiler",
        size: 200,
        minSize: 200,
        maxSize: 200,
        cell: ({ row }) => {
          return (
            <div className="text-truncate fw-medium fs-12">
              <div className="text-truncate">
                <i
                  className="bx bxs-user align-middle text-dark me-1"
                  id="tooltipTopActive"
                ></i>
                <b className="text-dark">{row.original.CustomerName}</b>
              </div>
              <div className="text-truncate">
                <i className="mdi mdi-phone align-middle text-dark me-1"></i>
                <b className="text-dark">
                  +90{" "}
                  {row.original.PhoneNumber?.toString().replace(
                    /(\d{3})(\d{3})(\d{2})(\d{2})/,
                    "$1 $2 $3 $4"
                  )}
                </b>
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor("CarCategory", {
        header: "Araç Bilgiler",
        size: 250,
        minSize: 250,
        maxSize: 250,
        cell: ({ row }) => {
          return (
            <div className="text-truncate fw-semibold">
              <div className="text-truncate fw-medium fs-12">
                <i
                  className="bx bxs-category align-middle text-dark me-1"
                  id="tooltipTopActive"
                ></i>
                <b className="text-dark">
                  {row.original.CarCategory ? row.original.CarCategory : "-"}
                </b>
              </div>
              <div className="text-truncate fw-medium fs-12">
                <i
                  className="bx bxs-car align-middle text-dark me-1"
                  id="tooltipTopActive"
                ></i>
                <b className="text-dark">
                  {row.original.CarBrand} - {row.original.CarModel}
                </b>
              </div>
              <div className="text-truncate fw-medium fs-12">
                <i
                  className="bx bxs-right-arrow align-middle text-dark me-1"
                  id="tooltipTopActive"
                ></i>
                <b className="text-dark">{row.original.CarPlate}</b>
              </div>
              <div className="text-truncate fw-medium fs-12">
                <i
                  className="mdi mdi-calendar-month align-middle text-dark me-1"
                  id="tooltipTopActive"
                ></i>
                <b className="text-dark">{row.original.CarYear}</b>
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor("StartDate", {
        header: "Tarih",
        size: 180,
        minSize: 180,
        maxSize: 180,
        cell: ({ row }) => {
          const startDate = moment(row.original.StartDate);
          const endDate = moment(row.original.EndDate);
          const today = moment();

          const totalDays = endDate.diff(startDate, "days");
          const remainingDays = endDate.diff(today, "days");
          const percentage = (remainingDays / totalDays) * 100;

          const getColor = () => {
            if (percentage <= 30) return "danger";
            if (percentage <= 70) return "warning";
            return "success";
          };

          return (
            <div className="text-truncate fw-semibold">
              <div className="d-flex fw-medium fs-12 text-muted">
                <i
                  className="ri-calendar-2-fill align-middle text-dark me-1"
                  id="tooltipTopActive"
                ></i>
                <div style={{ width: "65px" }}>S. Tarihi:</div>
                <b className="text-dark">
                  {moment(row.original.IssuedTime).format("DD.MM.YYYY")}
                </b>
              </div>
              <div className="d-flex fw-medium fs-12 text-muted">
                <i
                  className="ri-calendar-2-fill align-middle text-success me-1"
                  id="tooltipTopActive"
                ></i>
                <div style={{ width: "65px" }}>Başlangıç:</div>
                <b className="text-dark">
                  {moment(row.original.StartDate).format("DD.MM.YYYY")}
                </b>
              </div>
              <div className="d-flex fw-medium fs-12 text-muted">
                <i
                  className="ri-calendar-2-fill align-middle text-danger me-1"
                  id="tooltipTopActive"
                ></i>
                <div style={{ width: "65px" }}>Bitiş:</div>
                <b className="text-dark">
                  {moment(row.original.EndDate).format("DD.MM.YYYY")}
                </b>
              </div>
              <div className="d-flex fw-medium fs-12 text-muted">
                <i
                  className="ri-indeterminate-circle-fill align-middle text-info me-1"
                  id="tooltipTopActive"
                ></i>
                <div style={{ width: "65px" }}>Kalan:</div>
                <b className="text-danger">{remainingDays} Gün</b>
              </div>
              <Progress
                color={getColor()}
                value={percentage}
                striped
                animated
                className="mt-2"
                style={{ height: "8px" }}
              />
            </div>
          );
        },
      }),
    ],
    []
  );

  // Table
  const table = useReactTable({
    data: data,
    columns,
    state: {},
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
    getState,
  } = table;

  return (
    <Fragment>
      <Row>
        <Col xs={12}>
          <Card>
            <CardHeader className="align-items-center d-flex">
              <Button color="primary" className="me-3" onClick={onBack}>
                <i className="ri-arrow-left-line me-1"></i>
                Geri
              </Button>
              <div className="flex-grow-1">
                <h4 className="card-title mb-0">
                  <i className="ri-calendar-event-line me-2 text-danger"></i>
                  Yaklaşan Sözleşmeler
                </h4>
                <small className="text-muted">
                  30 gün içinde sona erecek aktif sözleşmeler (
                  {expiringAgreements?.length || 0} adet)
                </small>
              </div>
            </CardHeader>

            <CardBody>
              <Row className="mb-3">
                <CardBody className="border border-dashed border-end-0 border-start-0 border-top-0">
                  <form>
                    <div className="d-flex flex-wrap justify-content-between">
                      <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
                        <div style={{ width: "100px" }}>
                          <select
                            className="form-select mb-3"
                            aria-label="pageSize"
                            value={getState().pagination.pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                          >
                            <option defaultValue="10">10</option>
                            <option defaultValue="25">25</option>
                            <option defaultValue="50">50</option>
                            <option defaultValue="100">100</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <div
                          className="search-box me-2 mb-2 d-inline-block border"
                          style={{ borderRadius: "0.25em" }}
                        >
                          <DebouncedInput
                            value={globalFilter ?? ""}
                            onChange={(value) => setGlobalFilter(value)}
                            placeholder={"Ara..."}
                          />
                          <i className="bx bx-search-alt search-icon"></i>
                        </div>
                      </div>
                    </div>
                  </form>
                </CardBody>
              </Row>

              <div
                style={{
                  overflowX: "auto",
                }}
              >
                <Table className="align-middle">
                  <thead>
                    {getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className="text-muted"
                            style={{
                              width: header.column.columnDef.size,
                              minWidth: header.column.columnDef.minSize,
                              maxWidth: header.column.columnDef.maxSize,
                              letterSpacing: "0.8px",
                            }}
                          >
                            {header.isPlaceholder ? null : (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  cursor: "pointer",
                                  userSelect: "none",
                                }}
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                                {{
                                  asc: <i className="ri-arrow-down-s-line"></i>,
                                  desc: <i className="ri-arrow-up-s-line"></i>,
                                }[header.column.getIsSorted()] ?? null}
                              </div>
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody>
                    {expiringAgreementsLoading ? (
                      <tr>
                        <td colSpan="100%" className="text-center pt-5 pb-5">
                          <Spinner color="primary" />
                          <p className="text-dark">Yükleniyor...</p>
                        </td>
                      </tr>
                    ) : data.length === 0 ? (
                      <tr>
                        <td
                          colSpan="100%"
                          className="text-center pt-5 pb-5 text-muted"
                        >
                          <div className="avatar-md mx-auto mb-4">
                            <div className="avatar-title bg-success-subtle text-success rounded-circle fs-24">
                              <i className="ri-checkbox-circle-line"></i>
                            </div>
                          </div>
                          <h5 className="text-muted">
                            30 gün içinde sona erecek sözleşme bulunmamaktadır.
                          </h5>
                        </td>
                      </tr>
                    ) : (
                      getRowModel().rows.map((row) => {
                        return (
                          <tr key={row.id}>
                            {row.getVisibleCells().map((cell) => {
                              return (
                                <td
                                  key={cell.id}
                                  style={{
                                    width: cell.column.columnDef.size,
                                    minWidth: cell.column.columnDef.minSize,
                                    maxWidth: cell.column.columnDef.maxSize,
                                  }}
                                >
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>

              <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                <div className="col-sm">
                  <div className="text-muted">
                    {data.length > 0
                      ? `Toplam ${
                          table.getFilteredRowModel().rows.length
                        } kayıttan ${
                          table.getFilteredRowModel().rows.length === 0
                            ? 0
                            : table.getState().pagination.pageIndex *
                                table.getState().pagination.pageSize +
                              1
                        }
                  ile ${Math.min(
                    (table.getState().pagination.pageIndex + 1) *
                      table.getState().pagination.pageSize,
                    table.getFilteredRowModel().rows.length
                  )} arası gösteriliyor`
                      : "Kayıt Yok"}
                  </div>
                </div>
                {data.length > 0 && (
                  <div className="col-sm-auto">
                    <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
                      <li
                        className={
                          !getCanPreviousPage()
                            ? "page-item disabled"
                            : "page-item"
                        }
                      >
                        <Link
                          to="#"
                          className="page-link"
                          onClick={previousPage}
                        >
                          {"<"}
                        </Link>
                      </li>
                      {getPageOptions().map((item, key) => {
                        const pageIndex = getState().pagination.pageIndex;
                        const totalPages = getPageOptions().length;
                        const firstPage = item === 0;
                        const lastPage = item === totalPages - 1;
                        const nearCurrentPage = Math.abs(pageIndex - item) <= 1;

                        if (firstPage || lastPage || nearCurrentPage) {
                          return (
                            <React.Fragment key={key}>
                              <li className="page-item">
                                <Link
                                  to="#"
                                  className={
                                    pageIndex === item
                                      ? "page-link active"
                                      : "page-link"
                                  }
                                  onClick={() => setPageIndex(item)}
                                >
                                  {item + 1}
                                </Link>
                              </li>
                            </React.Fragment>
                          );
                        } else {
                          return null;
                        }
                      })}
                      <li
                        className={
                          !getCanNextPage() ? "page-item disabled" : "page-item"
                        }
                      >
                        <Link to="#" className="page-link" onClick={nextPage}>
                          {">"}
                        </Link>
                      </li>
                    </ul>
                  </div>
                )}
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default ExpiringAgreementsView;
